import {
    Component,
    ElementRef,
    forwardRef,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { take, tap } from 'rxjs/operators';

import {
    CoachingDiffDecorateConnector,
    CoachingDiffDecoratorService,
    DiffItem,
} from '../../coaching-base/diff-decorator';
import { EntityType, FocusDto } from '../../coaching-base/general';
import { CoachingSwitchboardService } from '../../coaching-base/switchboard';
import { ClickOptions } from '../../coaching-insight/click-options';
import { CoachingObservationDialogService } from '../../coaching-observation/coaching-observation-dialog.service';
import { Row } from '../../coaching-observation/experimental.grid';
import {
    ObservationFormDisplayProperties,
    ObservationMode,
    READONLY_OBSERVATION,
    ReadonlyObservationContainer,
} from '../../coaching-observation/observation/observation.model';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { TranslationKeyMap } from '../coaching-commitment.i18n';
import { sortObservationsByLinkTime } from '../commitment/commitment-dto.transformer';

import { ObservationFormValue } from './observations.commitment.form';
import {
    CellContext,
    makeRows,
    RowContext,
} from './observations.commitment.model';

const displayProperties: ObservationFormDisplayProperties = {
    hideBehaviour: true,
    hideContextMenu: true,
    hideEvaluations: true,
    hideFocusTooltip: true,
    hideNavigator: true,
    hideSubbehaviour: true,
    stopScrollHeight: true,
    diableExpandFocus: true,
};

@Component({
    selector: '[cmpsCommitmentObservations]',
    templateUrl: './observations.commitment.component.html',
    host: {
        class: 'cmps-commitment-observations',
        'data-e2e': 'component-commitment-observations',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ObservationsCommitmentComponent),
            multi: true,
        },
    ],
    viewProviders: [
        {
            provide: READONLY_OBSERVATION,
            useValue: ReadonlyObservationContainer,
        },
    ],
})
@TakeUntilDestroy()
export class ObservationsCommitmentComponent
    implements
        OnInit,
        OnDestroy,
        ControlValueAccessor,
        CoachingDiffDecorateConnector
{
    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _ObservationMode = ObservationMode;
    /** @private */ _displayProperties = displayProperties;
    /** @private */ _expandedObservation: number;
    @Input() focus: FocusDto;
    @Input() draftCommitmentId: string;
    @Input() commitmentId: number;
    /** @private */ _disabled: boolean;
    @Input() linkDisabled: boolean;

    private _onChange: (_: ObservationFormValue[]) => void;
    private _onTouched: () => void;
    /** @private */ _rows: Array<Row<RowContext, CellContext>>;

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _ngZone: NgZone,
        private readonly _translateService: TranslateService,
        private readonly _dialog: CmpsDialog,
        private readonly _switchboardService: CoachingSwitchboardService,
        private readonly _observationDialogService: CoachingObservationDialogService,
        private readonly _diffDecoratorService: CoachingDiffDecoratorService,
    ) {}

    ngOnInit(): void {
        this._diffDecoratorService.registerForEntityType(
            EntityType.CoachingEvent,
            this,
        );
    }

    ngOnDestroy(): void {
        this._diffDecoratorService.unRegisterForEntityType(
            EntityType.CoachingEvent,
            this,
        );
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._disabled = isDisabled;
    }

    writeValue(obj: ObservationFormValue[]): void {
        const items: ObservationFormValue[] = !obj
            ? []
            : sortObservationsByLinkTime(obj);
        this._rows = makeRows(items);

        this._ngZone.onStable.pipe(take(1)).subscribe({
            complete: () => this._diffDecoratorService.redecorate(this),
        });
    }

    ddcDecorate(
        diffItem: DiffItem,
        state: 'change' | 'accept' | 'dispute',
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const el = this._el.nativeElement.querySelector(
            `[data-diffitemid="${diffItem.diffItemId}"]`,
        ) as HTMLElement;
        if (!el) {
            return;
        }
        el.classList.add(`cmps-commitment-observations--dd-row-${state}`);
        const index = this._rows.findIndex(
            (e) => e.ctx.e.event.eventId === diffItem.entityId,
        );
        if (index === this._rows.length - 1) {
            el.classList.add(
                `cmps-commitment-observations--dd-row-last-${state}`,
            );
        }
    }

    ddcUnDecorate(
        diffItem: DiffItem,
        state: 'change' | 'accept' | 'dispute',
    ): void {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        const el = this._el.nativeElement.querySelector(
            `[data-diffitemid="${diffItem.diffItemId}"]`,
        ) as HTMLElement;
        if (!el) {
            return;
        }
        // for each class 'cause ie 11
        el.classList.remove(`cmps-commitment-observations--dd-row-${state}`);
        el.classList.remove(
            `cmps-commitment-observations--dd-row-last-${state}`,
        );
    }

    /** @private */ _onRemoveClick($event: RowContext): void {
        const next = this._rows.map((r) => r.ctx);
        const index = next.indexOf($event);
        if (index === -1) {
            return;
        }
        next.splice(index, 1);
        this._rows.splice(index, 1);
        this._rows = [...this._rows];
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._onChange && this._onChange(next.map((e) => e.e));
    }

    /** @private */ _onSelectClick(): void {
        if (this._disabled || this.linkDisabled) {
            return;
        }
        this.resolveConfirmationModal(
            TranslationKeyMap._9504_DO_YOU_WANT_TO_LINK_AN_OBSERVATION_,
            ClickOptions.LINKOBSERVATION,
        );
    }

    // yes, keep this explicit click handler
    /** @private */ _onNewClick(): void {
        if (this._disabled) {
            return;
        }
        this.resolveConfirmationModal(
            TranslationKeyMap._9505_DO_YOU_WANT_TO_CREATE_A_NEW_OBSERVATION_,
            ClickOptions.STARTOBSERVATION_FORCOMMMITMENT,
        );
    }

    /** @private */ _onToggleExpandClick(observationId: number): void {
        this._expandedObservation =
            this._expandedObservation === observationId ? null : observationId;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    resolveConfirmationModal(message: string, clickOption: number) {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9479_CONFIRMATION,
            ),
            message: this._translateService.instant(message),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret !== 0) {
                        return;
                    }

                    switch (clickOption) {
                        case ClickOptions.LINKOBSERVATION:
                            this._openReview();
                            break;
                        case ClickOptions.STARTOBSERVATION_FORCOMMMITMENT:
                            this._openNew();
                            break;
                        default:
                            console.log('No valid click option');
                            break;
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _openReview(): void {
        this._observationDialogService.openReview(null, true, this.focus, true);
    }

    private _openNew(): void {
        this._observationDialogService.openNew({
            focus: this.focus,
            draftCommitmentId: this.draftCommitmentId,
            commitmentId: this.commitmentId,
        });
    }
}
